import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Close";
import CopyAll from "@mui/icons-material/CopyAll";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { styled, Tooltip, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import {
  DataGrid,
  GridActionsCellItem,
  GridRowModes,
  GridToolbarContainer,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
  useGridApiRef,
} from "@mui/x-data-grid";
import Pagination from "@mui/material/Pagination";
import { randomId } from "@mui/x-data-grid-generator";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useRef, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import {
  deletewithQueryApiServices,
  postWithQueryApiServices,
  putWithQueryApiServices,
} from "../../apis/api";
import { errorToast, successToast } from "../../utils/helper";
import { CommonSearch } from "./CommonSearch";

const ActionContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 16px",
  borderBottom: "1px solid",
  borderColor: "rgb(224 224 224)",
  gap: "16px",
  flex: 1,
}));

// Styled wrapper for disabled state
const GridWrapper = styled(Box)(({ disabled }) => ({
  height: "100%",
  width: "100%",
  position: "relative",
  opacity: disabled ? 0.8 : 1,
  pointerEvents: "auto",
  cursor: disabled ? "default" : "auto",
  backgroundColor: disabled ? "#f8f9fa" : "transparent",
  borderRadius: "10px",
  "&::after": disabled
    ? {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.01)",
        pointerEvents: "none",
        zIndex: 1,
        borderRadius: "10px",
      }
    : {},
  "& .actions": {
    color: "text.secondary",
  },
  "& .textPrimary": {
    color: "text.primary",
  },
}));

function EditToolbar(props) {
  const {
    setRows,
    setRowModesModel,
    label,
    search,
    setSearch,
    columns,
    initialFormValue,
    handleAddNew,
    permission,
    isTenantBlocked,
    tenantMessage,
    disabled,
    internalIsEditing,
    onEditingStateChange,
    handleSelection,
  } = props;

  const handleClick = () => {
    if (isTenantBlocked) {
      errorToast(
        tenantMessage || "Your tenant is inactive. This action is not allowed.",
      );
      return;
    }

    if (disabled || internalIsEditing) {
      errorToast("Please complete the current operation first.");
      return;
    }

    if (handleAddNew) {
      handleAddNew();
      return;
    }

    const id = randomId();
    let newRow = {
      ...initialFormValue,
      id,
      isNew: true,
    };
    setRows((oldRows) => [newRow, ...oldRows]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: columns[0].field },
    }));

    if (onEditingStateChange) {
      onEditingStateChange(true);
    }

    if (handleSelection) {
      handleSelection(newRow);
    }
  };

  const isButtonDisabled = isTenantBlocked || disabled;

  if (!setSearch && !permission) return null;

  return (
    <GridToolbarContainer>
      <ActionContainer sx={{ justifyContent: setSearch ? "space-between" : "flex-end" }}>
        {setSearch && (
          <CommonSearch
            value={search}
            handleChange={(e) => setSearch(e.target.value)}
            disabled={disabled}
            style={{ width: "100%", marginBottom: "8px", marginTop: "10px" }}
          />
        )}
        {permission && (
          <Tooltip
            title={
              isTenantBlocked
                ? tenantMessage || "Your tenant is inactive"
                : disabled
                  ? "Complete the current operation first"
                  : ""
            }
            arrow
            disableHoverListener={!isButtonDisabled}
          >
            <span id={label ? `tour-add-${label.toLowerCase()}-btn` : undefined}>
              <Button
                color="primary"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleClick}
                disabled={isButtonDisabled}
                sx={{
                  cursor: isButtonDisabled ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                  marginTop: "10px",
                  marginBottom: "8px",
                }}
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Add New {label}
                </Box>
                <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                  Add
                </Box>
              </Button>
            </span>
          </Tooltip>
        )}
      </ActionContainer>
    </GridToolbarContainer>
  );
}

function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Box
      sx={{ display: "flex", justifyContent: "center", p: 1, width: "100%" }}
    >
      <Pagination
        color="primary"
        count={pageCount}
        page={page + 1}
        onChange={(event, value) => apiRef.current.setPage(value - 1)}
        size="small"
        siblingCount={0}
        boundaryCount={1}
        showFirstButton
        showLastButton
      />
    </Box>
  );
}

const BooleanEditCell = ({ params }) => {
  const { id, field, value, api, hasFocus } = params;

  const checkboxRef = React.useRef(null);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (hasFocus && checkboxRef.current) {
      checkboxRef.current.focus();
    }
  }, [hasFocus]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleKeyDown = (e) => {
      if (e.key === "Tab") {
        e.stopPropagation();
      }
    };

    container.addEventListener("keydown", handleKeyDown);
    return () => {
      container.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleChange = (event) => {
    const newValue = event.target.checked;
    api.setEditCellValue({ id, field, value: newValue });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.stopPropagation();
      event.preventDefault();
      const newValue = !value;
      api.setEditCellValue({ id, field, value: newValue });
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{ display: "flex", justifyContent: "center", width: "100%" }}
    >
      <Checkbox
        inputRef={checkboxRef}
        checked={!!value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        size="small"
        sx={{ p: 0 }}
        tabIndex={0}
        inputProps={{ tabIndex: 0 }}
      />
    </Box>
  );
};

const getNoRowsMessage = (label) => {
  if (!label) return "No items match";
  const clean = label.trim().toLowerCase();
  if (clean === "company" || clean === "companies") return "No companies match";
  if (clean === "category" || clean === "categories") return "No categories match";
  if (clean === "user" || clean === "users") return "No users match";
  if (clean === "invoice" || clean === "invoices") return "No invoices match";
  if (clean === "security profile" || clean === "security profiles" || clean === "securityprofile") return "No security profiles match";
  if (clean === "criteria") return "No criteria match";
  return `No ${clean} match`;
};

const CustomNoRowsOverlay = ({ label }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
    <Typography sx={{ color: 'text.secondary', fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}>
      {getNoRowsMessage(label)}
    </Typography>
  </Box>
);

const BottomLoader = () => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 1.5, borderTop: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
    <CircularProgress size={18} sx={{ color: "#7285b3" }} />
    <Typography variant="body2" sx={{ ml: 1, color: "text.secondary", fontFamily: "Poppins, sans-serif", fontSize: "13px", fontWeight: 500 }}>
      Loading more...
    </Typography>
  </Box>
);

export const FullFeaturedCrudGrid = ({
  initialRows,
  columns,
  label,
  handleSelection,
  search,
  setSearch,
  apiUrl,
  refetch,
  copyFun,
  addFunc,
  editFunc,
  initialFormValue,
  hideFooterPagination,
  rowUpdateFun,
  delFun,
  handleAddNew,
  hideEdit,
  permission = true,
  addNewTrigger = false,
  outerValue,
  setOuterValue,
  isTenantBlocked = false,
  tenantMessage = "Your tenant is inactive. This action is not allowed.",
  // Optional props for editing state tracking (backward compatible)
  onEditingStateChange = null,
  isEditing = false,
  disabled = false,
  isSaving = false, // ✅ NEW: Indicates custom save/loading state from parent
  // ✅ NEW: Optional props for cancel callback and controlled selection
  onCancelEdit = null, // Callback when edit is cancelled
  selectedRowId = null, // Controlled selected row ID for highlight
  extraActionsRenderer = null, // Optional: render extra controls in Actions column
  renderEditActions = null, // ✅ NEW: Optional: render controls next to Save/Cancel in edit mode
  onSuccess = null, // ✅ NEW: Callback after successful create/update
  autoHeight = false, // ✅ NEW: Enable auto-height for flexible layouts
  rowCount = undefined, // ✅ NEW: Total row count for server-side pagination
  paginationModel = undefined, // ✅ NEW: Pagination model for server-side pagination
  onPaginationModelChange = undefined, // ✅ NEW: Callback for pagination model changes
  // ✅ NEW: Granular Permissions
  canCreate = null,
  canUpdate = null,
  canDelete = null,
  actionColumnWidth = null, // ✅ NEW: Custom width for actions column
  isLocalTable = false, // ✅ NEW: Indicates if the grid is client-side only (local state)
  loading = false, // ✅ NEW: Indicates parent query loading state
  getRowClassName: getRowClassNameProp = null,
}) => {
  // Use granular permissions if provided, otherwise fallback to 'permission' prop
  const hasCreateAccess = canCreate !== null ? canCreate : permission;
  const hasUpdateAccess = (canUpdate !== null ? canUpdate : permission) && !hideEdit;
  const hasDeleteAccess = canDelete !== null ? canDelete : permission;

  const effectiveHideFooterPagination = (label?.toLowerCase() === "subscription" && rowCount !== undefined && rowCount > 200) ? false : hideFooterPagination;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [rows, setRows] = React.useState([]);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [internalIsEditing, setInternalIsEditing] = React.useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = React.useState(null);
  const [isScrollingFetching, setIsScrollingFetching] = React.useState(false);
  const { currentCompany, tenantString } = useOutletContext();
  const apiRef = useGridApiRef();
  const gridWrapperRef = useRef(null);
  const [isMounted, setIsMounted] = React.useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  const isFetchingRef = useRef(false);
  const hasMoreRef = useRef(true);

  useEffect(() => {
    isFetchingRef.current = false;
    setIsScrollingFetching(false);
  }, [initialRows]);

  useEffect(() => {
    if (paginationModel && paginationModel.page === 0) {
      hasMoreRef.current = true;
    }
  }, [paginationModel?.page]);

  // Combined disabled state
  const isDisabled = disabled || isTenantBlocked;

  // Track internal editing state and notify parent
  const updateEditingState = useCallback(
    (editing) => {
      setInternalIsEditing(editing);
      if (onEditingStateChange) {
        onEditingStateChange(editing);
      }
    },
    [onEditingStateChange],
  );

  // Check if any row is in edit mode
  const checkIfAnyRowEditing = useCallback((model) => {
    return Object.values(model).some((row) => row.mode === GridRowModes.Edit);
  }, []);

  const handleRowEditStop = (params, event) => {
    // Prevent ALL automatic row edit stops — editing should only end
    // programmatically via Save/Cancel buttons (matching handleRowEditStart pattern).
    // Without this, Tab/Enter/Escape keys auto-exit edit mode and trigger save.
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStart = (params, event) => {
    // Only allow editing to start programmatically (by clicking the Edit icon)
    // and prevent entering edit mode on any keyboard/mouse interaction triggers
    event.defaultMuiPrevented = true;
  };

  useEffect(() => {
    if (outerValue) {
      setRows(
        rows.map((obj) => {
          if (obj.id === outerValue.id) {
            return {
              ...obj,
              ...outerValue,
            };
          } else {
            return obj;
          }
        }),
      );
      setOuterValue(null);
    }
  }, [outerValue, rows, setOuterValue]);

  useEffect(() => {
    const activeEditId = Object.keys(rowModesModel).find(
      (id) => rowModesModel[id]?.mode === GridRowModes.Edit
    );
    if (!activeEditId || isLocalTable) return;

    const handleOutsideClick = (event) => {
      const isSelectPopup = event.target.closest('.MuiMenu-paper') || event.target.closest('.MuiPopover-root') || event.target.closest('.MuiDialog-root');
      if (isSelectPopup) return;

      if (gridWrapperRef.current && !gridWrapperRef.current.contains(event.target)) {
        setRowModesModel((prev) => ({
          ...prev,
          [activeEditId]: { mode: GridRowModes.View }
        }));
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [rowModesModel]);

  const paginationModelRef = useRef(paginationModel);
  useEffect(() => {
    paginationModelRef.current = paginationModel;
  }, [paginationModel]);

  useEffect(() => {
    if (initialRows !== undefined) {
      const currentModel = paginationModelRef.current;
      if (currentModel && initialRows.length < currentModel.pageSize) {
        hasMoreRef.current = false;
      } else {
        hasMoreRef.current = true;
      }
      const seen = new Set();
      const mappedNewRows = initialRows.map((obj, index) => {
        let calculatedId = obj.id || obj[`${label.toLowerCase()}ID`];
        if (!calculatedId || seen.has(calculatedId)) {
          calculatedId = `${calculatedId || 'temp'}_${index}`;
        }
        seen.add(calculatedId);
        return {
          ...obj,
          id: calculatedId,
        };
      });

      if (currentModel && currentModel.page > 0) {
        setRows((prevRows) => {
          const existingIds = new Set(prevRows.map(r => r.id));
          const uniqueNewRows = mappedNewRows.filter(r => !existingIds.has(r.id));
          return [...prevRows, ...uniqueNewRows];
        });
      } else {
        setRows(mappedNewRows);
        setRowModesModel((prev) => {
          const validIds = new Set(mappedNewRows.map((r) => r.id));
          const next = {};
          Object.entries(prev).forEach(([id, mode]) => {
            if (validIds.has(id)) next[id] = mode;
          });
          return next;
        });
      }
    }
  }, [initialRows, label]);


  // Expose React-level row selection helper for tour integration
  useEffect(() => {
    if (!window.reactTourHelpers) {
      window.reactTourHelpers = {};
    }
    if (label) {
      const gridLabel = label.toLowerCase();
      window.reactTourHelpers[gridLabel] = {
        selectFirstRow: () => {
          if (rows && rows.length > 0) {
            const firstRow = rows[0];
            if (handleSelection) {
              handleSelection(firstRow);
              return true;
            } 
          }
          return false;
        }
      };
    }

    return () => {
      if (label) {
        const gridLabel = label.toLowerCase();
        if (window.reactTourHelpers) {
          delete window.reactTourHelpers[gridLabel];
        }
      }
    };
  }, [rows, label, handleSelection]);

  useEffect(() => {
    if (addNewTrigger && !isTenantBlocked && !disabled) {
      if (handleAddNew) {
        handleAddNew();
      } else {
        const id = randomId();
        let newRow = {
          ...initialFormValue,
          id,
          isNew: true,
        };
        setRows((oldRows) => [newRow, ...oldRows]);
        setRowModesModel((oldModel) => ({
          ...oldModel,
          [id]: { mode: GridRowModes.Edit, fieldToFocus: columns[0].field },
        }));
        updateEditingState(true);
        if (handleSelection) {
          handleSelection(newRow);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addNewTrigger]);

  // Track row modes model changes to detect editing state
  useEffect(() => {
    const isAnyEditing = checkIfAnyRowEditing(rowModesModel);
    if (isAnyEditing !== internalIsEditing) {
      updateEditingState(isAnyEditing);
    }
  }, [
    rowModesModel,
    internalIsEditing,
    checkIfAnyRowEditing,
    updateEditingState,
  ]);

  // Capture phase Tab key handling to allow native focus movement to non-editable Actions cell
  useEffect(() => {
    const grid = gridWrapperRef.current;
    if (!grid) return;

    const handleKeyDown = (e) => {
      if (e.key === "Tab") {
        const activeEl = document.activeElement;
        if (
          activeEl &&
          (activeEl.classList.contains("MuiInputBase-input") ||
            activeEl.classList.contains("MuiSelect-select") ||
            activeEl.classList.contains("MuiSelect-nativeInput") ||
            activeEl.tagName === "INPUT" ||
            activeEl.tagName === "SELECT" ||
            activeEl.getAttribute("role") === "combobox" ||
            activeEl.getAttribute("role") === "button")
        ) {
          // Stop propagation so DataGrid's focus engine doesn't intercept Tab,
          // letting browser native focus flow natively to the Actions cell buttons
          e.stopPropagation();
        }
      }
    };

    grid.addEventListener("keydown", handleKeyDown, true);
    return () => {
      grid.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isMounted, internalIsEditing]);

  const handleEditClick = (id) => () => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    if (disabled) {
      errorToast("Please complete the current operation first.");
      return;
    }

    const selectedRow = rows.find((_) => _.id === id);
    handleSelection(selectedRow);
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    updateEditingState(true);
  };

  const handleCopyClick = (id) => () => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    if (disabled) {
      errorToast("Please complete the current operation first.");
      return;
    }

    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    copyFun(rows.filter((_) => _.id === id));
  };

  const handleSaveClick = (id) => () => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    if (apiRef.current) {
      apiRef.current.stopRowEditMode({ id });
    } else {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    }
  };

  const handleDeleteClick = (id) => () => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    if (disabled) {
      errorToast("Please complete the current operation first.");
      return;
    }

    setDeleteConfirmId(id);
  };

  const confirmDelete = () => {
    if (!deleteConfirmId) return;
    const id = deleteConfirmId;
    if (delFun) delFun(rows.filter((row) => row.id !== id));
    if (apiUrl) deleteApiCall.mutate(id);
    setRows(rows.filter((row) => row.id !== id));
    setDeleteConfirmId(null);
  };

  // ✅ UPDATED: handleCancelClick to call onCancelEdit
  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }

    // ✅ NEW: Call the cancel callback to clear selections in parent
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  const processRowUpdate = async (newRow, oldRow) => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      throw new Error(tenantMessage);
    }

    // Check if any of the columns' values actually changed
    const hasChanges = columns.some((col) => {
      const newVal = newRow[col.field];
      const oldVal = oldRow ? oldRow[col.field] : undefined;

      // Normalize values (null, undefined, empty string)
      const normalizedNew = newVal === undefined || newVal === null ? "" : String(newVal).trim();
      const normalizedOld = oldVal === undefined || oldVal === null ? "" : String(oldVal).trim();

      return normalizedNew !== normalizedOld;
    });

    if (!hasChanges) {
      if (newRow.isNew || (oldRow && oldRow.isNew)) {
        setRows((prev) => prev.filter((r) => r.id !== newRow.id));
        if (onCancelEdit) {
          onCancelEdit();
        }
      }
      updateEditingState(false);
      return oldRow || newRow;
    }

    // ✅ VALIDATION FIRST: Check required fields (runs even if rowUpdateFun exists)
    const requiredColumns = columns.filter((col) => col.required !== false);
    const columnNames = requiredColumns.map((obj) => obj.field);
    const missingFields = columnNames.filter(
      (key) => newRow[key] === undefined || newRow[key] === "",
    );

    if (missingFields.length > 0) {
      const missingFieldsMsg = missingFields
        .map((key) => {
          const col = columns.find((c) => c.field === key);
          return col && col.headerName ? col.headerName : key;
        })
        .join(", ");
      errorToast(`Please fill the following fields: ${missingFieldsMsg}`);
      throw new Error(`Validation failed: Missing fields ${missingFieldsMsg}`);
    }

    // ✅ VALIDATION: Check patterns (runs even if rowUpdateFun exists)
    for (const col of columns) {
      if (
        col.pattern &&
        newRow[col.field] !== undefined &&
        newRow[col.field] !== ""
      ) {
        const value = newRow[col.field];
        const regex =
          col.pattern instanceof RegExp ? col.pattern : new RegExp(col.pattern);
        if (!regex.test(value)) {
          const errMsg = col.patternError || `Invalid value for ${col.headerName || col.field}`;
          errorToast(errMsg);
          throw new Error(errMsg);
        }
      }
    }

    if (rowUpdateFun) {
      try {
        const result = await rowUpdateFun(newRow);
        // If this was a new row and save succeeded, remove the temporary row from local state.
        if (newRow.isNew && !isLocalTable) {
          setRows((prev) => prev.filter((r) => r.id !== newRow.id));
        }
        updateEditingState(false);
        return result;
      } catch (error) {
        // Re-throw to keep the row in edit mode and prevent losing form inputs
        throw error;
      }
    }

    const updatedRow = { ...newRow, isNew: false };
    try {
      if (newRow?.[`${label.toLowerCase()}ID`]) {
        const mergedRow = editFunc ? editFunc(updatedRow) : updatedRow;
        if (apiUrl) await updateApiCall.mutateAsync(mergedRow);
      } else {
        if (apiUrl) await createApiCall.mutateAsync(updatedRow);
      }
      
      // If this was a new row and save succeeded, remove the temporary row from local state.
      if (newRow.isNew && !isLocalTable) {
        setRows((prev) => prev.filter((r) => r.id !== newRow.id));
      }
      return updatedRow;
    } catch (error) {
      // Re-throw to keep the row in edit mode and prevent losing form inputs
      throw error;
    }
  };

  const updateApiCall = useMutation({
    mutationKey: [`update${label}form`],
    mutationFn: (newRow) =>
      putWithQueryApiServices(
        apiUrl,
        `selectedCompany=${currentCompany || ""}${tenantString || ""}`,
        newRow,
      ),
    onSuccess: (res, variables) => {
      refetch();
      successToast(
        `${label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()} Updated!`,
      );
      updateEditingState(false);
      if (onSuccess) onSuccess(variables);
    },
    onError: (error) => {
      if (error?.response?.data?.code !== "TENANT_NOT_ACTIVE") {
        errorToast(error.response?.data?.message || "Update failed");
      }
    },
  });

  const createApiCall = useMutation({
    mutationKey: [`create${label}form`],
    mutationFn: (newRow) =>
      postWithQueryApiServices(
        apiUrl,
        `selectedCompany=${currentCompany || ""}${tenantString || ""}`,
        newRow,
      ),
    onSuccess: (res, variables) => {
      if (variables?.id && !isLocalTable) {
        setRows((prev) => prev.filter((r) => r.id !== variables.id));
      }
      refetch();
      successToast(
        `${label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()} Created!`,
      );
      updateEditingState(false);
      if (onSuccess) onSuccess(res?.data || res);
    },
    onError: (error, variables) => {
      if (error?.response?.data?.code !== "TENANT_NOT_ACTIVE") {
        errorToast(error.response?.data?.message || "Create failed");
      }
    },
  });

  const deleteApiCall = useMutation({
    mutationKey: [`delete${label}Details`],
    mutationFn: (id) =>
      deletewithQueryApiServices(
        apiUrl,
        `selectedCompany=${currentCompany || ""}${tenantString || ""}`,
        id,
      ),
    onSuccess: () => {
      refetch();
      successToast(
        `${label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()} Deleted!`,
      );
      if (onCancelEdit) {
        onCancelEdit();
      }
      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      if (error?.response?.data?.code !== "TENANT_NOT_ACTIVE") {
        errorToast(error.response?.data?.message || "Delete failed");
      }
    },
  });

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleRowsScrollEnd = useCallback(() => {
    if (effectiveHideFooterPagination && onPaginationModelChange && paginationModel && !isFetchingRef.current && hasMoreRef.current) {
      isFetchingRef.current = true;
      setIsScrollingFetching(true);
      onPaginationModelChange((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    }
  }, [effectiveHideFooterPagination, onPaginationModelChange, paginationModel]);

  const handleRowsScrollEndRef = useRef(handleRowsScrollEnd);
  useEffect(() => {
    handleRowsScrollEndRef.current = handleRowsScrollEnd;
  }, [handleRowsScrollEnd]);

  useEffect(() => {
    if (!loading) {
      isFetchingRef.current = false;
      setIsScrollingFetching(false);
    }
  }, [loading]);

  useEffect(() => {
    if (!effectiveHideFooterPagination) return;
    
    let active = true;
    let virtualScroller = null;
    let lastScrollTime = 0;
    let lastScrollTop = 0;

    const handleScroll = (e) => {
      const now = Date.now();
      if (now - lastScrollTime < 100) return;
      lastScrollTime = now;

      const { scrollTop, scrollHeight, clientHeight } = e.target;

      if (isFetchingRef.current || !hasMoreRef.current) {
        lastScrollTop = scrollTop;
        return;
      }

      if (scrollHeight - scrollTop - clientHeight < 40) {
        handleRowsScrollEndRef.current();
      }

      lastScrollTop = scrollTop;
    };

    const setupListener = () => {
      if (!active) return;
      const el = gridWrapperRef.current?.querySelector(".MuiDataGrid-virtualScroller");
      if (el) {
        virtualScroller = el;
        virtualScroller.addEventListener("scroll", handleScroll);
      } else {
        setTimeout(setupListener, 50);
      }
    };

    setupListener();
      
    return () => {
      active = false;
      if (virtualScroller) {
        virtualScroller.removeEventListener("scroll", handleScroll);
      }
    };
  }, [effectiveHideFooterPagination]);

  // Row click is for SELECTION only
  const handleRowClick = (params) => {
    // Don't allow selection while editing or when disabled
    if (internalIsEditing) {
      return;
    }
    if (handleSelection) {
      handleSelection(params.row);
    }
  };

  const handleCellKeyDown = (params, event) => {
    if (event.key === "Enter" || event.key === " ") {
      // If we are currently editing this row, allow Space and Enter to work natively
      const isRowEditing = rowModesModel[params.id]?.mode === GridRowModes.Edit;
      if (isRowEditing) {
        return;
      }

      // Prevent default behavior (like page scroll on Space or edit mode on Enter)
      event.defaultMuiPrevented = true;
      if (event.key === " ") {
        event.preventDefault();
      }
      // Stop event propagation so it doesn't trigger other grid actions
      event.stopPropagation();

      // Trigger selection on Enter/Space
      if (
        !internalIsEditing &&
        handleSelection &&
        params.field !== "actions" &&
        params.field !== "gridActions"
      ) {
        handleSelection(params.row);
      }
    }
  };

  const actionColumn = extraActionsRenderer
    ? [
        {
          field: "gridActions",
          type: "actions",
          headerName: "Actions",
          width: actionColumnWidth || (isMobile ? 220 : 300),
          align: "center",
          headerAlign: "center",
          cellClassName: "actions",
          sortable: false,
          renderCell: (params) => {
            const id = params.row.id;
            const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
            const isSavingRow = updateApiCall.isPending || createApiCall.isPending || isSaving;

            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  width: "100%",
                }}
              >
                {/* Extra actions from parent - hidden during edit mode */}
                {!isInEditMode &&
                  !params.row.isNew &&
                  extraActionsRenderer(params, {
                    handleEditClick: handleEditClick(id),
                  })}

                {/* Built-in actions */}
                {isInEditMode ? (
                  <>
                    {renderEditActions && renderEditActions(params)}
                    <GridActionsCellItem
                      icon={<CancelIcon />}
                      label="Cancel"
                      className="textPrimary"
                      onClick={handleCancelClick(id)}
                      color="inherit"
                      disabled={isSavingRow}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Tab" || e.key === "Escape") {
                          e.stopPropagation();
                        }
                        if (e.key === "Enter") {
                          e.stopPropagation();
                          e.preventDefault();
                          handleCancelClick(id)();
                        }
                      }}
                    />
                    <Tooltip
                      title={isTenantBlocked ? tenantMessage : ""}
                      arrow
                      disableHoverListener={!isTenantBlocked}
                    >
                      <span>
                        <GridActionsCellItem
                          icon={isSavingRow ? <CircularProgress size={20} /> : <SaveIcon />}
                          label="Save"
                          className="textPrimary"
                          onClick={handleSaveClick(id)}
                          color="inherit"
                          disabled={isTenantBlocked || isSavingRow}
                          sx={{ cursor: (isTenantBlocked || isSavingRow) ? "not-allowed" : "pointer" }}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Tab" || e.key === "Escape") {
                              e.stopPropagation();
                            }
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              e.preventDefault();
                              handleSaveClick(id)();
                            }
                          }}
                        />
                      </span>
                    </Tooltip>
                  </>
                ) : (
                  hasDeleteAccess && (delFun || apiUrl) && (() => {
                    const hasMetadata = params.row && params.row.criteriaList && params.row.criteriaList.filter(c => c.criteria && c.criteria !== "Document Date" && c.criteria !== "").length > 0;
                    const hasDataSaved = params.row && (params.row.hasData === 1 || params.row.hasData === true || params.row.fileCount > 0 || hasMetadata);
                    const isDeleteDisabled = isDisabled || hasDataSaved;
                    const deleteTooltip = isTenantBlocked
                      ? tenantMessage
                      : hasDataSaved
                        ? "This category contains saved data and cannot be deleted"
                        : isDisabled
                          ? "Complete the current operation first"
                          : "";
                    return (
                      <Tooltip
                        title={deleteTooltip}
                        arrow
                        disableHoverListener={!deleteTooltip}
                      >
                        <span>
                          <GridActionsCellItem
                            icon={<DeleteIcon />}
                            label="Delete"
                            onClick={handleDeleteClick(id)}
                            color="inherit"
                            disabled={isDeleteDisabled}
                          />
                        </span>
                      </Tooltip>
                    );
                  })()
                )}
              </Box>
            );
          },
        },
      ]
    : [
        {
          field: "gridActions",
          type: "actions",
          headerName: "Actions",
          width: actionColumnWidth || 120,
          cellClassName: "actions",
          getActions: (params) => {
            const { id, row } = params;
            const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
            const isSavingRow = updateApiCall.isPending || createApiCall.isPending;
            const hasMetadata = row && row.criteriaList && row.criteriaList.filter(c => c.criteria && c.criteria !== "Document Date" && c.criteria !== "").length > 0;
            const hasDataSaved = row && (row.hasData === 1 || row.hasData === true || row.fileCount > 0 || hasMetadata);
            const isDeleteDisabled = isDisabled || hasDataSaved;
            const deleteTooltip = isTenantBlocked
              ? tenantMessage
              : hasDataSaved
                ? "This category contains saved data and cannot be deleted"
                : isDisabled
                  ? "Complete the current operation first"
                  : "";

            const deleteButton = (
              <Tooltip
                title={deleteTooltip}
                arrow
                disableHoverListener={!deleteTooltip}
              >
                <span>
                  <GridActionsCellItem
                    icon={<DeleteIcon />}
                    label="Delete"
                    onClick={handleDeleteClick(id)}
                    color="inherit"
                    disabled={isDeleteDisabled}
                    sx={{ cursor: isDeleteDisabled ? "not-allowed" : "pointer" }}
                    tabIndex={0}
                  />
                </span>
              </Tooltip>
            );

            const editButton = (
              <Tooltip
                title={
                  isDisabled
                    ? isTenantBlocked
                      ? tenantMessage
                      : "Complete the current operation first"
                    : ""
                }
                arrow
                disableHoverListener={!isDisabled}
              >
                <span>
                  <GridActionsCellItem
                    icon={<EditIcon />}
                    label="Edit"
                    className="textPrimary"
                    onClick={handleEditClick(id)}
                    color="inherit"
                    disabled={isDisabled}
                    sx={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                    tabIndex={0}
                  />
                </span>
              </Tooltip>
            );

            const saveButton = (
              <Tooltip
                title={
                  isTenantBlocked
                    ? tenantMessage || "Your tenant is inactive"
                    : disabled
                      ? "Complete the current operation first"
                      : ""
                }
                arrow
                disableHoverListener={!isTenantBlocked}
              >
                <span>
                  <GridActionsCellItem
                    icon={isSavingRow ? <CircularProgress size={20} /> : <SaveIcon />}
                    label="Save"
                    className="textPrimary"
                    onClick={handleSaveClick(id)}
                    color="inherit"
                    disabled={isTenantBlocked || isSavingRow}
                    sx={{ cursor: (isTenantBlocked || isSavingRow) ? "not-allowed" : "pointer" }}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Tab" || e.key === "Escape") {
                        e.stopPropagation();
                      }
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        e.preventDefault();
                        handleSaveClick(id)();
                      }
                    }}
                  />
                </span>
              </Tooltip>
            );

            const cancelButton = (
              <GridActionsCellItem
                icon={<CancelIcon />}
                label="Cancel"
                className="textPrimary"
                onClick={handleCancelClick(id)}
                color="inherit"
                disabled={isSavingRow}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Tab" || e.key === "Escape") {
                    e.stopPropagation();
                  }
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    e.preventDefault();
                    handleCancelClick(id)();
                  }
                }}
              />
            );

            const copyButton = (
              <Tooltip
                title={
                  isDisabled
                    ? isTenantBlocked
                      ? tenantMessage
                      : "Complete the current operation first"
                    : ""
                }
                arrow
                disableHoverListener={!isDisabled}
              >
                <span>
                  <GridActionsCellItem
                    icon={<CopyAll />}
                    label="Copy"
                    onClick={handleCopyClick(id)}
                    color="inherit"
                    disabled={isDisabled}
                    sx={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                    tabIndex={0}
                  />
                </span>
              </Tooltip>
            );

            if (isInEditMode) {
              return [cancelButton, saveButton];
            }

            const actions = [];
            if (copyFun) actions.push(copyButton);
            if (hasUpdateAccess) actions.push(editButton);
            if (hasDeleteAccess && (delFun || apiUrl)) actions.push(deleteButton);
            return actions;
          },
        },
      ];

  const modifiedColumns = React.useMemo(() => {
    return columns.map((col) => {
      const updatedCol = {
        ...col,
        editable: isDisabled ? false : col.editable,
        minWidth: col.minWidth !== undefined 
          ? col.minWidth 
          : Math.max(
              120, 
              col.headerName ? col.headerName.length * 8 + 30 : 100,
              col.flex ? col.flex * 120 : 0
            ),
      };

      if (col.type === "boolean") {
        updatedCol.renderEditCell = (params) => <BooleanEditCell params={params} />;
      }

      return updatedCol;
    });
  }, [columns, isDisabled]);

  // ✅ NEW: Compute row selection model based on selectedRowId prop
  const rowSelectionModel = React.useMemo(() => {
    if (selectedRowId !== null && selectedRowId !== undefined) {
      return [selectedRowId];
    }
    return [];
  }, [selectedRowId]);

  const finalColumns = React.useMemo(() => {
    const withoutBuiltInActions = modifiedColumns.filter(
      (col) => col.field !== "gridActions"
    );
    if (!(hasUpdateAccess || hasDeleteAccess)) {
      return withoutBuiltInActions;
    }
    return [...withoutBuiltInActions, ...actionColumn];
  }, [modifiedColumns, hasUpdateAccess, hasDeleteAccess, actionColumn]);



  return (
    <GridWrapper id={label ? `tour-${label.toLowerCase()}-grid` : undefined} ref={gridWrapperRef} disabled={disabled} sx={{ height: autoHeight ? "auto" : "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
        <DataGrid
        rows={rows}
        columns={finalColumns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          console.warn("Row update failed:", error.message);
        }}
        autoHeight={autoHeight}
        hideFooterSelectedRowCount={true}
        slots={{
          toolbar: EditToolbar,
          pagination: effectiveHideFooterPagination ? undefined : CustomPagination,
          noRowsOverlay: () => <CustomNoRowsOverlay label={label} />,
          footer: effectiveHideFooterPagination && isScrollingFetching ? BottomLoader : undefined,
        }}
        slotProps={{
          toolbar: {
            setRows,
            setRowModesModel,
            label,
            search,
            setSearch,
            columns,
            initialFormValue,
            handleAddNew,
            permission: hasCreateAccess,
            isTenantBlocked,
            tenantMessage,
            disabled,
            internalIsEditing,
            onEditingStateChange: updateEditingState,
            handleSelection,
          },
        }}
        onRowClick={handleRowClick}
        onCellKeyDown={handleCellKeyDown}
        hideFooter={effectiveHideFooterPagination}
        getRowClassName={(params) => {
          const base = params.row.dst === 0 ? "inactive-row" : "";
          const extra = getRowClassNameProp ? getRowClassNameProp(params) : "";
          return [base, extra].filter(Boolean).join(" ");
        }}
        sx={{
          width: "100%",
          fontSize: "1rem",
          cursor: disabled ? "not-allowed" : "auto",
          "& .inactive-row": {
            color: "#aaa !important",
            backgroundColor: "#f9f9f9 !important",
            "& .MuiDataGrid-cell": {
              color: "#aaa !important",
            }
          },
          "& .invoice-row-failed": {
            backgroundColor: "rgba(211, 47, 47, 0.08) !important",
          },
          "& .MuiDataGrid-row": {
            cursor: disabled ? "not-allowed" : "pointer",
          },
          "& .MuiDataGrid-cell": {
            cursor: "inherit",
          },
          // ✅ NEW: Style for controlled selected row
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor:
              selectedRowId !== null
                ? "rgba(25, 118, 210, 0.08)"
                : "transparent",
          },
          "& .MuiDataGrid-row.Mui-selected:hover": {
            backgroundColor:
              selectedRowId !== null
                ? "rgba(25, 118, 210, 0.12)"
                : "rgba(0, 0, 0, 0.04)",
          },
          // ✅ Sticky header overrides for autoHeight grids
          ...(autoHeight && {
            "&.MuiDataGrid-root": {
              overflow: "visible",
            },
            "& .MuiDataGrid-main": {
              overflow: "visible",
            },
            "& .MuiDataGrid-toolbarContainer": {
              position: "sticky",
              top: 0,
              zIndex: 3,
              backgroundColor: "#fff",
            },
            "& .MuiDataGrid-columnHeaders": {
              position: "sticky",
              top: (setSearch || hasCreateAccess) ? "70px" : 0,
              zIndex: 2,
              backgroundColor: "#fff",
              borderBottom: "1px solid rgba(224, 224, 224, 1)",
            },
          }),
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        apiRef={apiRef}
        isCellEditable={(params) => !isDisabled}
        disableColumnMenu={true}
        disableColumnResize={isDisabled}
        disableColumnSelector={true}
        // ✅ NEW: Control row selection via prop
        rowSelectionModel={rowSelectionModel}
        // Don't change selection on row click when controlled
        disableRowSelectionOnClick={selectedRowId !== null}
        sortingOrder={["asc", "desc"]}
        // ✅ Server-side pagination support
        paginationMode={effectiveHideFooterPagination ? "client" : (rowCount !== undefined ? "server" : "client")}
        rowCount={rowCount}
        paginationModel={effectiveHideFooterPagination ? undefined : (paginationModel || { page: 0, pageSize: 100 })}
        onPaginationModelChange={effectiveHideFooterPagination ? undefined : onPaginationModelChange}
      />
      </Box>
      {effectiveHideFooterPagination && isScrollingFetching && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 1.5, borderTop: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
          <CircularProgress size={18} sx={{ color: "#7285b3" }} />
          <Typography variant="body2" sx={{ ml: 1, color: "text.secondary", fontFamily: "Poppins, sans-serif", fontSize: "13px", fontWeight: 500 }}>
            Loading more...
          </Typography>
        </Box>
      )}
      <Dialog
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this {label || "item"}?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmId(null)} variant="outlined" size="small">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" size="small" disabled={deleteApiCall.isPending}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </GridWrapper>
  );
};
